import { Conversation } from '@twilio/conversations'
import { useMemo, FC } from 'react'

import { useConversationDisplayName } from 'features/MerinioChat/hooks'

import { Badge, Button as MerinioButton, Card, List } from 'ui'

import styles from './styles.module.scss'

import { t } from 'translations'

type Props = {
  conversations: Conversation[] | undefined
  setActiveConversation: (conversation: Conversation | null) => void
  conversationsUnreadMessagesMap: Map<string, number>
  conversationsLoading: boolean
}

const ConversationsList: FC<Props> = ({ conversations, setActiveConversation, conversationsUnreadMessagesMap, conversationsLoading }) => {
  const sortedConversations = useMemo(() => {
    if (!conversations) return []

    const data = [...conversations]
    return data.sort((a, b) => {
      const firstMessageCreated = a.lastMessage?.dateCreated?.getTime() ?? 0
      const secondMessageCreated = b.lastMessage?.dateCreated?.getTime() ?? 0
      if (firstMessageCreated > secondMessageCreated) {
        return -1
      }
      return 1
    })
  }, [conversations])

  if (conversationsLoading) return null

  return (
    <Card className={`mt-2 ${styles.conversationsList}`}>
      <Card.Body>
        <Card.Title>{t('chat.CONVERSATIONS.CONVERSATION_LIST')}</Card.Title>
        <List>
          {sortedConversations?.map(conversation => {
            return (
              <ConversationListItem
                unreadMessageCount={conversationsUnreadMessagesMap.get(conversation.sid) || 0}
                key={conversation.sid}
                conversation={conversation}
                setActiveConversation={setActiveConversation}
              />
            )
          })}
        </List>
      </Card.Body>
    </Card>
  )
}

type ConversationListItemProps = {
  conversation: Conversation
  setActiveConversation: (conversation: Conversation | null) => void
  unreadMessageCount: number
}

const ConversationListItem: FC<ConversationListItemProps> = ({ setActiveConversation, conversation, unreadMessageCount }): JSX.Element | null => {
  const { isLoading, displayName } = useConversationDisplayName(conversation)
  const isGroup = useMemo(() => {
    return conversation.attributes?.['type'] === 'group' ?? false
  }, [conversation.attributes])

  if (isLoading) return null

  return (
    <>
      <MerinioButton
        type="button"
        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
        onClick={() => setActiveConversation(conversation)}
      >
        {displayName}
        <div className="d-flex align-items-center">
          {isGroup ? <i className="fa-light fa-user-group mr-1" style={{ fontSize: 10 }} /> : null}
          <Badge variant="primary" pill className="text-white">
            {unreadMessageCount}
          </Badge>
        </div>
      </MerinioButton>
    </>
  )
}

export { ConversationsList }
